import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '@/types/auth.types';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  /** Full login — stores all three values. Used only by the login page. */
  setAuth: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  /** Validates a stored session on startup — updates user only, keeps existing tokens. */
  setInitialUser: (user: AuthUser) => void;
  /** Swaps both tokens after a successful refresh rotation. Does not touch the user. */
  rotateTokens: (accessToken: string, refreshToken: string) => void;
  clearAuth: () => void;
  setInitialized: () => void;
  /**
   * User Profile P1 — merges display-safe fields (name, photo URL) returned by
   * the profile resource into the persisted user, so the header reflects a
   * profile edit without a reload. Never touches tokens or authorization
   * fields; the server remains the authority on those.
   */
  updateUser: (patch: Partial<AuthUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isInitialized: false,

      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, isAuthenticated: true }),

      setInitialUser: (user) => set({ user, isAuthenticated: true }),

      rotateTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      clearAuth: () =>
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false }),

      setInitialized: () => set({ isInitialized: true }),

      updateUser: (patch) =>
        set((state) => (state.user ? { user: { ...state.user, ...patch } } : {})),
    }),
    {
      name: 'sp-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        // isInitialized intentionally excluded — always re-validate on startup
      }),
    },
  ),
);
